import $ from "jquery";

import toNativePromise from "JIX/utils/toNativePromise.js";
import RequestError from "JIX/errors/RequestError.js";
import ApiValidationError from "JIX/errors/ApiValidationError.js";
import ApiConflictError from "JIX/errors/ApiConflictError.js";

export function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = array[i];
        array[i] = array[j];
        array[j] = temp;
    }
    return array;
}

export function uniqueArray(array) {
    return array.filter((value, index, self) => self.indexOf(value) === index);
}

/**
 * Return a string with the concatenation of individual name parts. Expects an
 * object with the properties `fullname`, `middlename` and `lastname` (which
 * may be undefined or null).
 */
export function concatName({ firstname, middlename, lastname }) {
    return [firstname, middlename, lastname].join(" ").trim();
}

/**
 * Performs named substitution of parameters.
 * @param {string} str - The format string
 * @param {Object} args - A map from named parameter to string
 * @returns {string} Returns the format-substituted string
 *
 * Replaces all occurences of the key.
 *
 * Equivalent to JIX.Gettext.__ x(), * but without translations.
 *
 * @example
 * format("Hello {name}, you {title}!", { name: "Dave", title: "chap" })
 */
export function format(str, args) {
    return Object.keys(args).reduce(
        (acc, key) => acc.replace(new RegExp("{" + key + "}", 'g'), args[key]),
        str
    );
}

export function getCurrencyFormatter(currency, locale = getLocale()) {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 0 });
}

export function getNumberFormatter(locale = getLocale()) {
    return new Intl.NumberFormat(locale);
}

export function formatByteSize(size, toType = 'MB', fromType = 'B') {
    let convRatio = {
        'B':  0,
        'KB': 1,
        'MB': 2,
        'GB': 3,
        'TB': 4,
    };
    let ratio = convRatio[fromType] - convRatio[toType];
    return (size * Math.pow(1024, ratio)).toFixed(2);
}

/**
 * Performs API call as a promise
 * @param {string} method - HTTP method for the the API call
 * @param {string} url - Endpoint for API call
 * @param {Object} data - Data associated with API call
 * @returns {Promise} - response from API endpoint
 */
export function api(method, url, data = {}, options = {}) {
    const ajaxOptions = {
        method,
        url,
        data,
        traditional: true,
        headers: {},
        ...options,
    };

    if (window.Stash && window.Stash.common && window.Stash.common.csrf_token) {
        ajaxOptions.headers["X-CSRF-Token"] = window.Stash.common.csrf_token;
    }

    return toNativePromise($.ajax(ajaxOptions))
        .catch(xhr => {
            if (xhr.status === 422) {
                throw new ApiValidationError(method, url, xhr);
            } else if (xhr.status === 409) {
                throw new ApiConflictError(method, url, xhr);
            } else {
                throw new RequestError(method, url, xhr);
            }
        });
}

export function loadScript(url, options = {}) {
    return new Promise((resolve, reject) => {
        const parentElement = options.parent || document.head;

        const script = document.createElement('script');
        script.src = url;

        script.async = !!(options.async);
        script.defer = !!(options.defer);

        script.onload = resolve;
        script.onerror = reject;

        parentElement.appendChild(script);
    });
}

export const RESPONSIVE_BREAKPOINTS = ['xs', 'sm', 'md', 'lg', 'xl'];

export function getResponsiveBreakpoint() {
    const width = window.innerWidth;
    if (width >= 1200) {
        return 'xl';
    } else if (width >= 992) {
        return 'lg';
    } else if (width >= 768) {
        return 'md';
    } else if (width >= 576) {
        return 'sm';
    } else {
        return 'xs';
    }
}

/**
 * Compares a given breakpoint to the current one
 *
 * @param {string} str - The comparison breakpoint
 * @returns {number} A comparison
 *
 * Returns -1 if the breakpoint is lower, returns 0 if the breakpoint is
 * equal, and returns 1 if the breakpoint is greater.
 */
export function compareResponsiveBreakpoint(breakpoint) {
    const idx = RESPONSIVE_BREAKPOINTS.indexOf(breakpoint);
    if (idx < 0) {
        throw Error('Invalid breakpoint');
    }

    const currentBreakpoint = getResponsiveBreakpoint();
    const curIdx = RESPONSIVE_BREAKPOINTS.indexOf(currentBreakpoint);

    return Math.sign(curIdx - idx);
}

export function getLocale(defaultLocale = 'en-GB') {
    return document.querySelector('html').getAttribute('lang') || defaultLocale;
}

export function randomInteger(min = 0, max = Number.MAX_SAFE_INTEGER) {
    return Math.random() * (max - min) + min;
}

export function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent;
}

export function truncateString(str, maxLength, replacement = '…') {
    if (str.length <= maxLength) {
        return str;
    }
    const cut = maxLength - replacement.length;
    return [...str].slice(0, cut).join('') + replacement;
}
